import React, { useState, useEffect, useCallback } from 'react';
import { useWeatherApi } from '../../context/WeatherApiContext';
import { useAutomation } from '../../hooks/useAutomation';
import { useFirebase } from '../../hooks/useFirebase';
import { storage } from '../../utils/storage';
import { FanMode } from '../../utils/automation';
import { WeatherCard } from './WeatherCard';
import { ForecastCard } from './ForecastCard';
import { HourlyForecastCard } from './HourlyForecastCard';
import { FanControlCard } from './FanControlCard';

export const DashboardView: React.FC = () => {
  const { weatherData, status } = useWeatherApi();
  const { saveReading } = useFirebase();
  const [fanState, setFanState] = useState(false);
  
  // Load automation settings
  const automationConfig = storage.loadAutomationConfig();
  const [automationEnabled] = useState(automationConfig?.enabled ?? false);
  const [fanMode] = useState<FanMode>((automationConfig?.mode as FanMode) ?? 'hot');

  // Handle fan commands (store in localStorage and Firebase since no hardware)
  const handleFanCommand = useCallback((command: 'ON' | 'OFF', isAuto: boolean) => {
    const newState = command === 'ON';
    setFanState(newState);
    
    // Store fan state in localStorage
    localStorage.setItem('fanState', JSON.stringify(newState));
    
    console.log(`✅ Fan ${command}${isAuto ? ' (automated)' : ' (manual)'}`);

    // Persist the updated fan status to Firebase
    const timestamp = Date.now();
    const reading = {
      temperature: weatherData?.temperature ?? null,
      humidity: weatherData?.humidity ?? null,
      pressure: weatherData?.pressure ?? null,
      light: weatherData?.light ?? null,
      rain: weatherData?.rain ?? null,
      windSpeed: weatherData?.windSpeed ?? null,
      windDirection: weatherData?.windDirection ?? null,
      fan: newState,
      timestamp,
    };
    saveReading(reading);
    return true;
  }, [saveReading, weatherData]);

  // Use automation hook
  useAutomation({
    enabled: automationEnabled,
    mode: fanMode,
    weatherData: weatherData || null,
    onFanCommand: handleFanCommand,
  });

  // Load fan state from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('fanState');
    if (saved !== null) {
      setFanState(JSON.parse(saved));
    }
  }, []);

  // Update fan state from weather data if present
  useEffect(() => {
    if (weatherData?.fan !== undefined) {
      setFanState(weatherData.fan);
    }
  }, [weatherData?.fan]);

  return (
    <div 
      className="p-6 space-y-6" 
      style={{ 
        backgroundColor: '#1a1a1f',
        minHeight: '100vh',
      }}
    >
      {/* Main Weather Cards - Side by Side */}
      <div className="space-y-6">
      <WeatherCard weatherData={weatherData} />
        <FanControlCard
          fanState={fanState}
          isConnected={status.connected}
          onCommand={(cmd) => handleFanCommand(cmd, false)}
        />
      </div>

      {/* Bottom Section: Forecast Cards - Responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ForecastCard />
        <HourlyForecastCard />
      </div>
    </div>
  );
};
